import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, Stat, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  UploadSimple, CheckSquare, X, ArrowUUpLeft,
  Bank as BankIcon, MagnifyingGlass, Lightbulb, ListChecks,
} from "@phosphor-icons/react";

export default function BankPage() {
  const { activeCompany, companies, formatApiError } = useAuth();
  const [tab, setTab] = useState("holding");
  const [txns, setTxns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [bankAccount, setBankAccount] = useState("");
  const [search, setSearch] = useState("");

  // Bulk selection state
  const [selected, setSelected] = useState(() => new Set());
  const [bulkGL, setBulkGL] = useState("");

  // Suggestion cache: txId → {account_id, code, name}
  const [suggestions, setSuggestions] = useState({});

  // Post-single dialog state (existing)
  const [postTxn, setPostTxn] = useState(null);
  const [postKind, setPostKind] = useState("customer");
  const [postCustomer, setPostCustomer] = useState("");
  const [postSupplier, setPostSupplier] = useState("");
  const [postInvoiceId, setPostInvoiceId] = useState("");
  const [postGLAccount, setPostGLAccount] = useState("");

  // Intercompany post state
  const [icCounterparty, setIcCounterparty] = useState("");
  const [icOurGlCode, setIcOurGlCode] = useState("");
  const [icOurGlName, setIcOurGlName] = useState("");
  const [icTheirGlCode, setIcTheirGlCode] = useState("");
  const [icTheirGlName, setIcTheirGlName] = useState("");

  const load = async () => {
    if (!activeCompany) return;
    const [t, a, c, s, inv] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/bank/transactions?status=${tab}`),
      api.get(`/companies/${activeCompany.id}/accounts`),
      api.get(`/companies/${activeCompany.id}/customers`).catch(() => ({ data: [] })),
      api.get(`/companies/${activeCompany.id}/suppliers`).catch(() => ({ data: [] })),
      api.get(`/companies/${activeCompany.id}/invoices`).catch(() => ({ data: [] })),
    ]);
    setTxns(t.data);
    setAccounts(a.data);
    setCustomers(c.data || []);
    setSuppliers(s.data || []);
    setInvoices(inv.data || []);
    setSelected(new Set());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompany?.id, tab]);

  // Pre-fetch suggestions for holding items (based on description tokens seen before)
  useEffect(() => {
    if (tab !== "holding" || !txns.length || !activeCompany) return;
    let cancelled = false;
    (async () => {
      const out = {};
      // Limit calls to avoid flooding — first 100 items
      for (const t of txns.slice(0, 100)) {
        try {
          const { data } = await api.get(
            `/companies/${activeCompany.id}/bank/holding/suggest`,
            { params: { description: t.description || "" } }
          );
          if (data.suggestion) out[t.id] = data.suggestion;
        } catch (e) { /* ignore */ }
      }
      if (!cancelled) setSuggestions(out);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [txns, activeCompany?.id, tab]);

  // ---------- Filtering ----------
  const filtered = useMemo(() => {
    if (!search.trim()) return txns;
    const s = search.toLowerCase();
    return txns.filter((t) =>
      (t.description || "").toLowerCase().includes(s) ||
      String(t.amount || "").includes(s) ||
      (t.date || "").includes(s)
    );
  }, [txns, search]);

  const holdingCount = tab === "holding" ? txns.length : 0;

  // ---------- Selection ----------
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const allSelected = filtered.length > 0 && filtered.every((t) => selected.has(t.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((t) => t.id)));
  };

  // ---------- Upload ----------
  const doImport = async (force = false) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("bank_account_id", bankAccount);
    if (force) fd.append("force", "true");
    const { data } = await api.post(`/companies/${activeCompany.id}/bank/import`, fd);
    toast.success(`Imported ${data.imported} · Auto-matched ${data.auto_matched || 0} · Holding ${data.holding ?? data.imported}`);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    load();
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !bankAccount) return toast.error("Choose a file and bank account");
    try {
      await doImport(false);
    } catch (e2) {
      // 409 = duplicate — ask user to confirm re-import
      if (e2.response?.status === 409 && e2.response?.data?.duplicate) {
        const prev = e2.response.data.previous_import || {};
        const type = e2.response.data.match_type;
        const header =
          type === "identical"
            ? `You already imported this exact file on ${String(prev.imported_at || "").slice(0, 10)}.`
            : `${prev.overlap_pct}% of these transactions match "${prev.filename}" imported on ${String(prev.imported_at || "").slice(0, 10)}.`;
        const msg =
          `${header}\n\nPrevious import:\n` +
          `  file: ${prev.filename || "—"}\n` +
          `  when: ${String(prev.imported_at || "").slice(0, 16).replace("T", " ")}\n` +
          `  by:   ${prev.imported_by || "—"}\n` +
          `  txns: ${prev.transaction_count ?? "—"}` +
          `\n\nImport anyway?`;
        if (!window.confirm(msg)) return;
        try {
          await doImport(true);
        } catch (e3) {
          toast.error(formatApiError(e3.response?.data?.detail) || e3.message);
        }
        return;
      }
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  // ---------- Bulk post ----------
  const bulkPost = async () => {
    if (!bulkGL) return toast.error("Choose a GL account for bulk posting");
    if (!selected.size) return toast.error("Select at least one item");
    if (!window.confirm(`Post ${selected.size} selected item(s) to ${accounts.find(a => a.id === bulkGL)?.code} · ${accounts.find(a => a.id === bulkGL)?.name}?`)) return;
    try {
      const { data } = await api.post(
        `/companies/${activeCompany.id}/bank/holding/bulk-post-gl`,
        { ids: Array.from(selected), account_id: bulkGL }
      );
      const msg = `Posted ${data.posted} · Failed ${data.failed}`;
      if (data.failed) toast.warning(msg);
      else toast.success(msg);
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // ---------- Single row-level actions (existing pattern) ----------
  const openPost = (t) => {
    setPostTxn(t);
    setPostKind(t.amount >= 0 ? "customer" : "supplier");
    setPostCustomer(""); setPostSupplier(""); setPostInvoiceId(""); setPostGLAccount("");
    setIcCounterparty(""); setIcOurGlCode(""); setIcOurGlName("");
    setIcTheirGlCode(""); setIcTheirGlName("");
  };
  const submitPost = async () => {
    if (!postTxn) return;
    // Intercompany posting has its own endpoint
    if (postKind === "intercompany") {
      if (!icCounterparty) return toast.error("Select the counterparty company");
      try {
        await api.post(
          `/companies/${activeCompany.id}/bank/holding/${postTxn.id}/post-intercompany`,
          {
            counterparty_company_id: icCounterparty,
            our_ic_gl_code: icOurGlCode || null,
            our_ic_gl_name: icOurGlName || null,
            their_ic_gl_code: icTheirGlCode || null,
            their_ic_gl_name: icTheirGlName || null,
          }
        );
        toast.success("Posted as intercompany — journals on both companies + Loans tab updated");
        setPostTxn(null); load();
      } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || e2.message); }
      return;
    }
    const body = { kind: postKind };
    if (postKind === "customer") {
      if (!postCustomer) return toast.error("Select a customer");
      body.customer_id = postCustomer;
      if (postInvoiceId) body.invoice_id = postInvoiceId;
    } else if (postKind === "supplier") {
      if (!postSupplier) return toast.error("Select a supplier");
      body.supplier_id = postSupplier;
      if (postInvoiceId) body.invoice_id = postInvoiceId;
    } else if (postKind === "gl") {
      if (!postGLAccount) return toast.error("Select a GL account");
      body.account_id = postGLAccount;
    }
    try {
      await api.post(`/companies/${activeCompany.id}/bank/holding/${postTxn.id}/post`, body);
      toast.success("Posted to ledger");
      setPostTxn(null); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || e2.message); }
  };
  const ignore = async (t) => {
    try { await api.post(`/companies/${activeCompany.id}/bank/holding/${t.id}/ignore`);
          toast.success("Item ignored"); load(); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || e2.message); }
  };
  const unignore = async (t) => {
    try { await api.post(`/companies/${activeCompany.id}/bank/holding/${t.id}/unignore`);
          toast.success("Restored to holding"); load(); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || e2.message); }
  };

  const applySuggestion = async (t) => {
    const sug = suggestions[t.id];
    if (!sug) return;
    if (!window.confirm(`Post to ${sug.code} · ${sug.name}? (Based on previous "${sug.sample_description}".)`)) return;
    try {
      await api.post(`/companies/${activeCompany.id}/bank/holding/${t.id}/post`,
        { kind: "gl", account_id: sug.account_id });
      toast.success("Posted using suggestion");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openInvoicesForParty = useMemo(() => {
    if (postKind === "customer" && postCustomer)
      return invoices.filter((i) => i.customer_id === postCustomer && i.type === "AR" && (i.payment_status || i.status) !== "paid");
    if (postKind === "supplier" && postSupplier)
      return invoices.filter((i) => i.supplier_id === postSupplier && i.type === "AP" && (i.payment_status || i.status) !== "paid");
    return [];
  }, [postKind, postCustomer, postSupplier, invoices]);

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;
  const bankAccountsList = accounts.filter((a) => a.type === "asset");

  return (
    <>
      <PageHeader
        eyebrow="Reconciliation"
        title="Bank Holding Area"
        description="Import statements. Search, select multiple, then bulk-post to the same GL. Previously-seen merchants get a suggested account."
      />
      <PageBody>
        {/* Import form */}
        <form onSubmit={upload} className="qb-card p-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-xs font-medium">Bank account</Label>
            <Select value={bankAccount} onValueChange={setBankAccount}>
              <SelectTrigger data-testid="bank-account-select" className="rounded-md mt-1"><SelectValue placeholder="Select bank cash account" /></SelectTrigger>
              <SelectContent>
                {bankAccountsList.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium">File (CSV / OFX / QFX / PDF)</Label>
            <Input ref={fileInputRef} type="file" accept=".csv,.ofx,.qfx,.pdf" data-testid="bank-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-md mt-1" />
          </div>
          <Button type="submit" data-testid="bank-import-btn" className="rounded-md h-10"><UploadSimple size={14} className="mr-2" /> Import statement</Button>
        </form>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Remaining to post" value={holdingCount} icon={BankIcon} tone={holdingCount > 0 ? "negative" : "positive"} testId="stat-remaining" />
          <Stat label="Selected" value={selected.size} testId="stat-selected" />
          <Stat label="Auto-suggestions" value={Object.keys(suggestions).length} icon={Lightbulb} testId="stat-suggestions" />
          <Stat label="Currently viewing" value={filtered.length} testId="stat-viewing" />
        </div>

        {/* Tabs + search + bulk bar */}
        <div className="qb-card p-3 flex flex-wrap items-center justify-between gap-3">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-md">
              <TabsTrigger value="holding" data-testid="tab-holding">Holding{holdingCount > 0 ? ` (${holdingCount})` : ""}</TabsTrigger>
              <TabsTrigger value="posted" data-testid="tab-posted">Posted</TabsTrigger>
              <TabsTrigger value="ignored" data-testid="tab-ignored">Ignored</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <MagnifyingGlass size={16} className="text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by description, amount, date…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-md h-9 w-72"
              data-testid="bank-search"
            />
          </div>
        </div>

        {tab === "holding" && (
          <div className="qb-card p-3 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-56">
              <Label className="text-xs font-medium">Bulk-post selected to GL account</Label>
              <Select value={bulkGL} onValueChange={setBulkGL}>
                <SelectTrigger data-testid="bulk-gl" className="rounded-md mt-1"><SelectValue placeholder="Choose ledger + code" /></SelectTrigger>
                <SelectContent>
                  {accounts.filter((a) => !bankAccountsList.some((b) => b.id === a.id)).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.code} · {a.name} <span className="text-muted-foreground">[{a.type}]</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={bulkPost}
              disabled={!selected.size || !bulkGL}
              data-testid="bulk-post-btn"
              className="rounded-md gap-2 h-10"
            >
              <ListChecks size={16} weight="fill" />
              Post {selected.size || 0} to Ledger
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="qb-card overflow-hidden">
          <table className="w-full qb-table" data-testid="bank-table">
            <thead>
              <tr>
                {tab === "holding" && (
                  <th className="w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="w-4 h-4 accent-primary cursor-pointer"
                      data-testid="select-all"
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th>Date</th>
                <th>Description</th>
                <th className="text-right">Amount</th>
                <th>Status</th>
                {tab === "holding" && <th>Suggestion</th>}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const sug = suggestions[t.id];
                return (
                  <tr key={t.id} data-testid={`bank-row-${t.id}`}>
                    {tab === "holding" && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(t.id)}
                          onChange={() => toggleOne(t.id)}
                          className="w-4 h-4 accent-primary cursor-pointer"
                          data-testid={`sel-${t.id}`}
                          aria-label={`Select ${t.description}`}
                        />
                      </td>
                    )}
                    <td className="num">{t.date}</td>
                    <td>
                      {t.description}
                      {t.auto_matched && <span className="ml-2 tag-badge text-primary">auto-matched</span>}
                    </td>
                    <td className={`text-right num ${t.amount < 0 ? "text-destructive" : "text-primary"}`}>
                      {fmtMoney(t.amount, activeCompany.currency)}
                    </td>
                    <td>
                      <span className={`tag-badge ${t.status === "posted" ? "text-primary" : t.status === "ignored" ? "text-muted-foreground" : "text-foreground"}`}>
                        {t.status || (t.journal_id ? "posted" : "holding")}
                      </span>
                    </td>
                    {tab === "holding" && (
                      <td>
                        {sug ? (
                          <button
                            type="button"
                            onClick={() => applySuggestion(t)}
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                            data-testid={`sug-${t.id}`}
                            title={`Based on previous "${sug.sample_description}"`}
                          >
                            <Lightbulb size={12} weight="fill" />
                            {sug.code} · {sug.name}
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                    <td className="text-right whitespace-nowrap">
                      {tab === "holding" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openPost(t)} data-testid={`bank-post-${t.id}`} className="rounded-md h-8 mr-1">
                            <CheckSquare size={12} className="mr-1" /> Post
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => ignore(t)} data-testid={`bank-ignore-${t.id}`} className="rounded-md h-8 text-muted-foreground">
                            <X size={12} />
                          </Button>
                        </>
                      )}
                      {tab === "ignored" && (
                        <Button size="sm" variant="outline" onClick={() => unignore(t)} data-testid={`bank-unignore-${t.id}`} className="rounded-md h-8">
                          <ArrowUUpLeft size={12} className="mr-1" /> Restore
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr>
                  <td colSpan={tab === "holding" ? 7 : 6} className="text-center py-10 text-muted-foreground">
                    {search ? "No matches for your search." : `No ${tab} bank items.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Existing single-post dialog */}
        <Dialog open={!!postTxn} onOpenChange={(v) => !v && setPostTxn(null)}>
          <DialogContent className="rounded-lg max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Post Bank Item</DialogTitle>
            </DialogHeader>
            {postTxn && (
              <div className="space-y-4">
                <div className="qb-card p-3 text-sm space-y-1">
                  <div><span className="overline mr-2">Date</span><span className="num">{postTxn.date}</span></div>
                  <div><span className="overline mr-2">Description</span><span>{postTxn.description}</span></div>
                  <div><span className="overline mr-2">Amount</span><span className="num">{fmtMoney(postTxn.amount, activeCompany.currency)}</span></div>
                </div>
                <div>
                  <Label className="text-xs font-medium">Post to</Label>
                  <Select value={postKind} onValueChange={setPostKind}>
                    <SelectTrigger data-testid="post-kind-select" className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer account</SelectItem>
                      <SelectItem value="supplier">Creditor / supplier account</SelectItem>
                      <SelectItem value="gl">General ledger account</SelectItem>
                      <SelectItem value="intercompany">Intercompany (another company)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {postKind === "customer" && (
                  <>
                    <div>
                      <Label className="text-xs font-medium">Customer</Label>
                      <Select value={postCustomer} onValueChange={(v) => { setPostCustomer(v); setPostInvoiceId(""); }}>
                        <SelectTrigger data-testid="post-customer-select" className="rounded-md mt-1"><SelectValue placeholder="Select customer" /></SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {openInvoicesForParty.length > 0 && (
                      <div>
                        <Label className="text-xs font-medium">Settle specific invoice (optional)</Label>
                        <Select value={postInvoiceId} onValueChange={setPostInvoiceId}>
                          <SelectTrigger data-testid="post-invoice-select" className="rounded-md mt-1"><SelectValue placeholder="— No specific invoice —" /></SelectTrigger>
                          <SelectContent>
                            {openInvoicesForParty.map((i) => (
                              <SelectItem key={i.id} value={i.id}>{i.invoice_number || i.id.slice(0, 6)} · {fmtMoney(i.total, activeCompany.currency)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
                {postKind === "supplier" && (
                  <>
                    <div>
                      <Label className="text-xs font-medium">Supplier</Label>
                      <Select value={postSupplier} onValueChange={(v) => { setPostSupplier(v); setPostInvoiceId(""); }}>
                        <SelectTrigger data-testid="post-supplier-select" className="rounded-md mt-1"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {openInvoicesForParty.length > 0 && (
                      <div>
                        <Label className="text-xs font-medium">Settle specific bill (optional)</Label>
                        <Select value={postInvoiceId} onValueChange={setPostInvoiceId}>
                          <SelectTrigger data-testid="post-invoice-select" className="rounded-md mt-1"><SelectValue placeholder="— No specific bill —" /></SelectTrigger>
                          <SelectContent>
                            {openInvoicesForParty.map((i) => (
                              <SelectItem key={i.id} value={i.id}>{i.invoice_number || i.id.slice(0, 6)} · {fmtMoney(i.total, activeCompany.currency)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
                {postKind === "gl" && (
                  <div>
                    <Label className="text-xs font-medium">GL account</Label>
                    <Select value={postGLAccount} onValueChange={setPostGLAccount}>
                      <SelectTrigger data-testid="post-gl-select" className="rounded-md mt-1"><SelectValue placeholder={postTxn.amount >= 0 ? "Select revenue/other account" : "Select expense account"} /></SelectTrigger>
                      <SelectContent>
                        {accounts.filter((a) => a.id !== postTxn.bank_account_id).map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.code} · {a.name} [{a.type}]</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {postKind === "intercompany" && (
                  <div className="space-y-3">
                    <div className="text-xs text-muted-foreground border border-primary/30 bg-primary/5 rounded-md p-2">
                      Posts journals on <b>both</b> companies and creates a matching entry on the <b>Intercompany Loans</b> tab. Direction is inferred from the bank amount — {postTxn.amount < 0 ? "you're the lender." : "you're the borrower."}
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Counterparty company</Label>
                      <Select value={icCounterparty} onValueChange={setIcCounterparty}>
                        <SelectTrigger data-testid="ic-counterparty-select" className="rounded-md mt-1"><SelectValue placeholder="Select the other company" /></SelectTrigger>
                        <SelectContent>
                          {(companies || []).filter((c) => c.id !== activeCompany.id).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-xs text-muted-foreground ml-1">[{c.role}]</span></SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="qb-card p-3 space-y-3">
                      <div className="overline">Intercompany GL Accounts (optional — leave blank to auto-generate)</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase text-muted-foreground">Our GL Code</Label>
                          <Input value={icOurGlCode} onChange={(e) => setIcOurGlCode(e.target.value)}
                                 placeholder="e.g. 1301" className="rounded-md num" data-testid="ic-our-gl-code" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-muted-foreground">Our GL Name</Label>
                          <Input value={icOurGlName} onChange={(e) => setIcOurGlName(e.target.value)}
                                 placeholder="Optional" className="rounded-md" data-testid="ic-our-gl-name" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-muted-foreground">Their GL Code</Label>
                          <Input value={icTheirGlCode} onChange={(e) => setIcTheirGlCode(e.target.value)}
                                 placeholder="e.g. 2301" className="rounded-md num" data-testid="ic-their-gl-code" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-muted-foreground">Their GL Name</Label>
                          <Input value={icTheirGlName} onChange={(e) => setIcTheirGlName(e.target.value)}
                                 placeholder="Optional" className="rounded-md" data-testid="ic-their-gl-name" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={submitPost} data-testid="bank-post-confirm" className="rounded-md">Post to Ledger</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageBody>
    </>
  );
}
